'use client';

import type { Theme, SxProps } from '@mui/material/styles';
import type { AuthUser } from 'src/auth/context/auth-context-value';

import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { usePopover } from 'minimal-shared/hooks';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Button, { buttonClasses } from '@mui/material/Button';

import { paths } from 'src/routes/paths';

import { getWorkspaceRoleBadge } from 'src/utils/workspace/workspace-permission';
import {
  sortWorkspacesForList,
  formatWorkspacePlanLabel,
} from 'src/utils/workspace/workspace-list-sort';

import { RADIUS } from 'src/theme/ui-tokens';
import { fsCall } from 'src/actions/firestore/dispatch';
import { workspaceKeys } from 'src/query-keys/workspace';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { CustomPopover } from 'src/components/custom-popover';
import { Scrollbar as ScrollbarBase } from 'src/components/scrollbar';

import { useSwitchWorkspace } from 'src/sections/common/hooks/handler/use-switch-workspace';

import { useAuthContext } from 'src/auth/hooks/use-auth-context';


// ----------------------------------------------------------------------

// Scrollbar 는 아직 JS 라 props 가 전부 필수로 추론된다 — 실제 계약(전부 선택)으로 좁혀 쓴다 (로직 무변)
const Scrollbar = ScrollbarBase as unknown as React.ComponentType<{
  sx?: SxProps<Theme>;
  fillContent?: boolean;
  children?: React.ReactNode;
  className?: string;
}>;

type WorkspacesPopoverProps = React.ComponentProps<typeof ButtonBase> & {
  data?: any[];
  user?: AuthUser | null;
};

/**
 * 줄 끝의 딱지 둘(역할·플랜) — **작게 둔다.**
 * ⚠️ 기본 크기(24)면 딱지가 이름만큼 눈에 띄어 목록이 「딱지 목록」으로 읽힌다.
 */
const BADGE_SX = { height: 18, px: 0.625, typography: 'uiMeta', fontWeight: 700, flexShrink: 0 } as const;

/** 플랜 딱지 — 영문 대문자라 같은 크기면 역할 딱지보다 커 보여 10px · 600 · 좁은 자간으로 한 단 더 줄인다 */
const PLAN_BADGE_SX = { ...BADGE_SX, fontSize: 10, fontWeight: 600, letterSpacing: '-0.02em' } as const;

export function WorkspacesPopover({ data = [], user, sx, ...other }: WorkspacesPopoverProps) {
  const router = useRouter();
  const mediaQuery = 'sm';

  const { open, anchorEl, onClose, onOpen } = usePopover();
  const { authenticated } = useAuthContext();
  const { switchTo } = useSwitchWorkspace();

  /**
   * 어드민이 둘러보는 **전체 목록은 스위처를 열 때** 읽는다.
   *
   * ⚠️ `enabled` 를 떼면 스위처를 열지도 않은 접속까지 워크스페이스 전량을 싣는다.
   * «열렸을 때만» 나가게 하고 5분 캐시로 재사용한다.
   * 어드민이 아니면 이 쿼리는 아예 돌지 않고, `data`(내가 멤버인 목록)가 그대로 쓰인다.
   */
  const adminListQuery = useQuery({
    queryKey: workspaceKeys.adminList(),
    queryFn: () => fsCall('workspace.adminList.get', {}),
    enabled: open && user?.isAdmin === true,
    staleTime: 5 * 60 * 1000,
  });

  const listData = user?.isAdmin === true && adminListQuery.data ? adminListQuery.data : data;

  // 만료일 기준 그룹화 + 플랜 우선순위 정렬 (Brand > Growth > Individual > Trial)
  const sortedWorkspaces = useMemo(
    () => sortWorkspacesForList(listData),
    [listData],
  );

  const currentWorkspace = user?.lastWorkspace
    ? sortedWorkspaces.find((item) => item.id === user.lastWorkspace) || sortedWorkspaces[0]
    : sortedWorkspaces[0];

  const [workspace, setWorkspace] = useState(currentWorkspace);

  useEffect(() => {
    const newCurrentWorkspace = user?.lastWorkspace
      ? sortedWorkspaces.find((item) => item.id === user.lastWorkspace) || sortedWorkspaces[0]
      : sortedWorkspaces[0];
    setWorkspace(newCurrentWorkspace);
  }, [user, sortedWorkspaces]);


  const handleChangeWorkspace = useCallback(
    (newValue: any) => {
      setWorkspace(newValue);
      onClose();
    },
    [onClose]
  );


  /**
   * 고른 워크스페이스를 사용자 문서에 기록한다.
   *
   * ⚠️ **이미 그 값이면 쓰지 않는다.** 이 효과는 위 효과가 목록을 받아 `workspace` 를
   * 채우기만 해도 돈다 — 가드를 빼면 스위처를 건드리지도 않은 마운트마다 **같은 값을
   * 되쓰는 Firestore 쓰기**가 나간다. 「사용자가 고른 것」과 「목록이 늦게 채워진 것」을
   * 구별하는 자리가 여기다. 쓰기는 설정 머리의 전환기와 같은 한 길(`useSwitchWorkspace`)이다.
   */
  useEffect(() => {
    if (!workspace || !authenticated) return;
    if (workspace.id === user?.lastWorkspace) return;
    switchTo(workspace.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace]);

  const buttonBg = {
    height: 1,
    zIndex: -1,
    opacity: 0,
    content: "''",
    borderRadius: RADIUS.sm,
    position: 'absolute',
    visibility: 'hidden',
    bgcolor: 'action.hover',
    width: 'calc(100% + 8px)',
    transition: (theme: Theme) =>
      theme.transitions.create(['opacity', 'visibility'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    ...(open && {
      opacity: 1,
      visibility: 'visible',
    }),
  };

  const isExpiredPlan = workspace?.subscriptionPlan?.planStatus === 'expired';
  /** 「Brand」 · 만료면 「Brand 만료」 — 드롭다운 목록과 같은 말을 쓴다 */
  const planLabel = formatWorkspacePlanLabel(workspace?.subscriptionPlan);

  const renderButton = () => (
    <ButtonBase
      disableRipple
      onClick={onOpen}
      sx={[
        {
          // 🚨 **고를 수 있는 것처럼 보이게 테두리를 두른다** — 글자만 있으면 그냥 제목으로 읽혀
          //   워크스페이스를 바꿀 수 있다는 것을 아무도 모른다.
          py: 0.5,
          px: 1,
          gap: 1,
          minWidth: 0,
          maxWidth: '100%',
          width: '100%',
          justifyContent: 'flex-start',
          borderRadius: RADIUS.sm,
          border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'action.hover' },
          '&::before': buttonBg,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >

      {/*
        🔑 **플랜은 워크스페이스 것이다** — 이름과 한 줄에 선다. 발치 계정 줄에 두면
          사람의 플랜처럼 읽히고, 워크스페이스를 바꿀 때 내 이름 밑 글자가 바뀐다.
        ⚠️ 줄어드는 쪽은 **이름**이다 — 플랜은 짧고 고정이라 먼저 줄이면 무슨 플랜인지 사라진다.
      */}
      <Box
        component="span"
        sx={{
          // ⚠️ `inline-flex` 면 칸이 좁을 때 글자마다 끊어진다 — 한 줄로 두고 넘치면 말줄임한다
          fontSize: 12.5,
          fontWeight: 600,
          lineHeight: 1.5,
          display: 'block',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          textAlign: 'left',
        }}
      >
        {workspace?.name}
      </Box>

      {planLabel && (
        <Box
          component="span"
          sx={{
            fontSize: 11.5,
            fontWeight: 600,
            lineHeight: 1.5,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            // 🚨 만료는 주의색으로 — 이름 옆 「만료」 표를 없앤 자리를 이 색이 대신한다
            color: isExpiredPlan ? 'warning.dark' : 'primary.main',
          }}
        >
          {planLabel}
        </Box>
      )}

      <Iconify width={16} icon="solar:alt-arrow-down-linear" sx={{ color: 'text.disabled', flexShrink: 0 }} />
    </ButtonBase>
  );

  const renderMenuList = (isAdmin = false) => {
    // 데이터 검증 및 로깅
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (sortedWorkspaces && sortedWorkspaces.length > 0) {
        const ids = sortedWorkspaces.map((item) => item.id);
        const uniqueIds = new Set(ids);

        if (ids.length !== uniqueIds.size) {
          console.warn('WorkspacesPopover: 중복된 ID가 발견되었습니다:', {
            total: ids.length,
            unique: uniqueIds.size,
            duplicates: ids.filter((id, index) => ids.indexOf(id) !== index),
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return (
      <CustomPopover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        slotProps={{
          arrow: { placement: 'top-left' },
          /**
           * 🔑 **단추 왼쪽 끝에 맞춰 선다** — 한때 왼쪽으로 더 밀어 두어(`ml: -1.55`) 화면 가장자리에
           *   붙고 단추보다 튀어나왔다. 여는 자리와 열린 것이 같은 선에서 시작해야 이어져 보인다.
           * ⚠️ 폭은 **이름이 읽히는 만큼**이다 — 350 은 이름 뒤에 빈 자리가 길어 딱지 둘이 멀어진다.
           */
          paper: { sx: { mt: 0.5, width: 292 } },
        }}
      >
        {/* ⚠️ 줄이 스물을 넘는 일이 흔하다 — 창 높이의 절반까지만 펴고 나머지는 구른다 */}
        <Scrollbar sx={{ maxHeight: '56vh' }}>
          <MenuList sx={{ py: 0.5 }}>
            {sortedWorkspaces.map((option, index) => {
              const roleBadge = getWorkspaceRoleBadge(user, option);

              return (
                <MenuItem
                  key={`${option.id}-${index}`}
                  selected={option.id === workspace?.id}
                  onClick={() => {
                    // if (option.subscriptionPlan.planStatus === 'expired') {
                    //   router.push(`${paths.subscription.plan}?type=upgrade`);
                    //   return;
                    // }
                    handleChangeWorkspace(option);
                  }}
                  /* ⚠️ 48 은 목록이 길 때 화면을 다 먹는다 — 딱지(20)가 들어가는 최소 높이로 둔다 */
                  sx={{ minHeight: 0, height: 34, gap: 1, px: 1.5 }}
                >
                  <Typography
                    noWrap
                    component="span"
                    sx={{ flexGrow: 1, minWidth: 0, typography: 'uiBody', fontWeight: 'fontWeightMedium' }}
                  >
                    {option.name}
                  </Typography>
                  <Label color={roleBadge.color} sx={BADGE_SX}>{roleBadge.label}</Label>
                  {option.subscriptionPlan && (
                    <Label
                      color={option.subscriptionPlan.planStatus === 'expired' ? 'error' : 'info'}
                      sx={PLAN_BADGE_SX}
                    >
                      {formatWorkspacePlanLabel(option.subscriptionPlan)}
                    </Label>
                  )}
                </MenuItem>
              );
            })}
          </MenuList>
        </Scrollbar>



        {isAdmin && (
          <>
            <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />
            <Button
              fullWidth
              startIcon={<Iconify width={18} icon="solar:add-linear" />}
              onClick={() => {
                onClose();
                router.push(`${paths.subscription.plan}`);
              }}
              sx={{
                gap: 2,
                justifyContent: 'flex-start',
                fontWeight: 'fontWeightMedium',
                [`& .${buttonClasses.startIcon}`]: {
                  m: 0,
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              }}
            >
              워크스페이스 생성
            </Button>
          </>
        )}

      </CustomPopover>
    );
  };

  return (
    <>
      {renderButton()}
      {renderMenuList()}
    </>
  );
}
